import { Injectable } from '@angular/core';

import { HttpHeaders, HttpClient, HttpErrorResponse } from '@angular/common/http';
import { throwError, Observable } from 'rxjs';
import { catchError, tap, map } from 'rxjs/operators';

export interface MacroValues {
  text: string;
  value: string;
  unit: string;
}

export interface RDAStat {
  symbol: string;
  pctDailyValue: number;
}

export interface PickerQuantity {
  text: string;
  value: string;
  columnIndex?: number;
}

export interface PickerMeasure {
  text: string;
  value: number;
  columnIndex?: number;
}

export interface PickerSelected {
  quantity: PickerQuantity;
  measure: PickerMeasure;
}

export enum NutrientType {
  Vitamin,
  Mineral,
  Macro
}

export interface Desc {
  ndbno: string;
  name: string;
  ds: string;
  manu: string;
  ru: string;
}

export interface Measure {
  label: string;
  eqv: number;
  eunit: string;
  qty: number;
  value: string;
}

export interface Nutrient {
  /**
   * Id or number of the nutrient, such as '205'
   */
  nutrient_id: string;
  name: string;
  derivation: string;
  group: string;
  unit: string;
  value: string;
  measures: Measure[];
}

export interface Food {
  sr: string;
  type: string;
  desc: Desc;
  group?: string;
  nutrients: Nutrient[];
  footnotes: any[];
}

const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' })
  // headers: new HttpHeaders({ 'Content-Type': 'application/json' ,'Access-Control-Allow-Origin':'*'})
};

//03/2020 the older usda api is EOL, new one is here:
//https://fdc.nal.usda.gov/api-guide.html
const newKeyForNewAPI = 'kAOTITbJipzh3IU3QuQwZcOC4eiFpBuAvRhh7xRR';

const apiUrl = "https://api.nal.usda.gov/ndb/nutrients/?format=json&api_key=f8NvYUOkoUH4SMzAJuYdv6gM6glJgNYzrmdacQph";

// const apiUrlV2 = "https://api.nal.usda.gov/ndb/V2/reports?format=json&api_key=f8NvYUOkoUH4SMzAJuYdv6gM6glJgNYzrmdacQph";
// const apiUrlV2 = "https://api.nal.usda.gov/ndb/V2/reports?format=json&api_key=f8NvYUOkoUH4SMzAJuYdv6gM6glJgNYzrmdacQph&type=f";
const apiUrlV2 = "https://api.nal.usda.gov/ndb/V2/reports?format=json&api_key=f8NvYUOkoUH4SMzAJuYdv6gM6glJgNYzrmdacQph";

//https://ndb.nal.usda.gov/ndb/doc/apilist/API-NUTRIENT-REPORT.md
// const apiUrlNutrients = 'https://api.nal.usda.gov/ndb/nutrients/?format=json&api_key=f8NvYUOkoUH4SMzAJuYdv6gM6glJgNYzrmdacQph&fg=0100&fg=0500';
const apiUrlNutrients = 'https://api.nal.usda.gov/ndb/nutrients/?format=json&api_key=f8NvYUOkoUH4SMzAJuYdv6gM6glJgNYzrmdacQph';

// const apiUrlV2 = "https://api.nal.usda.gov/ndb/V2/reports?format=json&api_key=f8NvYUOkoUH4SMzAJuYdv6gM6glJgNYzrmdacQph&type=f";

// https://ndb.nal.usda.gov/ndb/doc/apilist/API-SEARCH.md

//standard foods
const searchApi = "https://api.nal.usda.gov/ndb/search/?format=json&api_key=f8NvYUOkoUH4SMzAJuYdv6gM6glJgNYzrmdacQph&ds=Standard%20Reference&sort=n&"

//branded foods
// const searchApi = "https://api.nal.usda.gov/ndb/search/?format=json&api_key=f8NvYUOkoUH4SMzAJuYdv6gM6glJgNYzrmdacQph&ds=Branded%20Food%20Products&sort=r&"



@Injectable({
  providedIn: 'root'
})
export class UsdaService {

  macroValues = ['291', '269', '300', '203', '204'];

  constructor(private http: HttpClient) { }

  private handleError(error: HttpErrorResponse) {
    console.log('error: ' + JSON.stringify(error));
    if (error.error instanceof ErrorEvent) {
      // A client-side or network error occurred. Handle it accordingly.
      console.error('An error occurred:', error.error.message);
    } else {
      // The backend returned an unsuccessful response code.
      // The response body may contain clues as to what went wrong,
      console.error(
        `Backend returned code ${error.status}, ` +
        `body was: ${error.error}`);
    }
    // return an observable with a user-facing error message
    return throwError('Something bad happened; please try again later.');
  }

  public getNutrient(food: Food, nutrientNumber: string): Nutrient {

    let foundNutrient: Nutrient;
    let fromRdaMap;

    food.nutrients.forEach(nutrient => {
      // fromRdaMap = this.rda.get(nutrient.nutrient_id);
      // console.log('here with nut: ' + );
      if (nutrient.nutrient_id == nutrientNumber) {
        foundNutrient = nutrient;
      }
    });

    return foundNutrient;
  }

  /**
   * Get the macro stats 
   * @param food food
   * @param quantity quanity
   * @param measureIndex index of measure used
   */
  getMacroStats(food: Food, quantity: number, measureIndex: number): Map<string, MacroValues> {
    let macroMap = new Map<string, MacroValues>();
    let mapVal;

    this.macroValues.forEach(element => {
      mapVal = this.getNutrient(food, element);

      if (mapVal) {
        if (quantity >= 0 && measureIndex >= 0) {

          if (mapVal.measures[measureIndex] && mapVal.measures[measureIndex].value) {

            macroMap.set(element, {
              text: mapVal.name,
              value: Math.round((mapVal.measures[measureIndex].value * quantity)).toString(),
              unit: mapVal.measures[measureIndex].eunit
            });
          }

        } else {
          macroMap.set(element, { text: mapVal.name, value: mapVal.value, unit: mapVal.unit });
        }
      }
    });

    console.log('map size: ' + macroMap.size);
    return macroMap;
  }

  /**
   * Calculate the RDA for a given foods nutrients
   * @param food The food
   * @param filterBy which nutrients to filter by
   * @param selected if set it means user selected some custom measurements else uses per 100g rda
   */
  public calculateRDA(food: Food, filterBy: NutrientType, selected: PickerSelected = undefined): Map<string, number> {
    // console.log('Calculating stats for: ' + filterBy);

    let toReturn = new Map<string, number>();
    let fromRdaMap;

    food.nutrients.forEach(nutrient => {
      fromRdaMap = this.rda.get(nutrient.nutrient_id);
      if (fromRdaMap && fromRdaMap.type == filterBy) {
        if (selected) {
          let measure: number = (Number(nutrient.measures[selected.measure.value].value));
          let quantity: number = Number(selected.quantity.value);
          // console.log('Nut: ' + nutrient.name + ' a: ' + measure + ' x: ' + quantity);
          toReturn.set(fromRdaMap.symbol, Math.round(Number((measure * quantity) / fromRdaMap.value) * 100));
        } else {
          if (filterBy == NutrientType.Macro) {
            // console.log('in else with: ' + fromRdaMap.longName);
          }
          toReturn.set(fromRdaMap.symbol, Math.round((Number(nutrient.value) / fromRdaMap.value) * 100));
        }
      }
    });

    return toReturn;
  }

  /**
   * Search the remote USDA db/api
   * @param search Search query
   * @param offset Where to start in the list
   */
  // public searchRemoteFood(search: string, offset: number = 0): Observable<any> {
  public searchRemoteFood(search: string, offset: number = 1): Observable<any> {

    console.log('***here in search');

    // this.http.po
    let searchUrl = searchApi + 'offset=' + offset + '&max=25&q=' + search;
    console.log('search url: ' + searchUrl);

    /*return this.http.get(searchUrl, httpOptions).pipe(
      map(this.extractData),
      catchError(this.handleError));*/
      let newUrl = 'https://api.nal.usda.gov/fdc/v1/search?api_key=' + newKeyForNewAPI;

      // "includeDataTypes":{"Survey (FNDDS)":true,"Foundation":true,"Branded":false}

    let postData = {
      "generalSearchInput":"raw +broccoli",
      "pageNumber": offset
    }

    return this.http.post(newUrl, postData, httpOptions).pipe(
      map(this.extractData),
      catchError(this.handleError));


  }

  /**
   * Get a list of foods high in a particular nutrient
   * @param nutrientNumber Nutrient number
   */
  public getFoodsHighIn(nutrientNumber: string): Observable<any> {
    let limitToFoodGroups = '&fg=0100&fg=0400&fg=0500&fg=1000&fg=1100&fg=1200&fg=1300&fg=1500&fg=1600';
    let maxItemsToReturn = '&max=15&sort=c';

    let url = apiUrlNutrients + '&nutrients=' + nutrientNumber + limitToFoodGroups + maxItemsToReturn;

    console.log('nutrient search url: ' + url);


    return this.http.get(url, httpOptions).pipe(
      map(this.extractData),
      catchError(this.handleError));
  }

  /**
   * Get the details for a food
   * @param ndbno the ndbno of the food
   */
  public getRemoteFoodDetails(ndbno: string): Observable<any> {

    /*console.log('using url: ' + apiUrlV2 + '&ndbno=' + ndbno);
    return this.http.get(apiUrlV2 + '&ndbno=' + ndbno, httpOptions).pipe(
      map(this.extractData),
      catchError(this.handleError));*/

      let newUrl = 'https://api.nal.usda.gov/fdc/v1/' + ndbno + '?api_key=' + newKeyForNewAPI;
      // https://api.nal.usda.gov/fdc/v1/######?api_key=MY_API_KEY

      return this.http.get(newUrl, httpOptions).pipe(
        map(this.extractData),
        catchError(this.handleError));

  }

  private extractData(res: Response) {
    let body = res;

    return body || {};
  }

  //  essential: if true means it can't be produced by the body so need it in diet
  // the body doesn't create vitamins
  //fatSoluble: true, means hangs around in body, else water soluble:
  //water-soluble, which means they dissolve in water and other bodily fluids, so any amount of these nutrients that your body doesn’t use gets excreted in urine, Angelone explains. “That means vitamin B6 is needed every day since the body can’t store water-soluble vitamins.”

  public rda = new Map([
    // ["320", {value: 900, symbol: 'A (RAE)'}], //duplicates
    ["318", {
      value: 5000, symbol: 'A', longName: 'Vitamin A (IU)',
      type: NutrientType.Vitamin,
      fatSoluble: true,
      essential: true,
      recommended: { men: "900mcg", women: "700mcg", children: "300-600mcg" },
      blurb: "Is important for many bodily functions, including proper vision, a strong immune system, reproduction and good skin health",
      highDoses: "Changes to vision, bone pain, and skin changes, chronic toxicity can lead to liver damage and increased pressure on your brain",
      benefits: [
        { "desc": "Helps preserve eyesight" },
        { "desc": "May lower risk of certain cancers" },
        { "desc": "Supports a healthy immune system" },
        { "desc": "Reduces risk of acne" },
        { "desc": "Supports bone health" },
        { "desc": "Promotes healthy growth and reproduction" },
      ],
      deficiencies: [
        { "desc": "Night blindness" },
        { "desc": "Dry skin" },
        { "desc": "Dry eyes" },
        { "desc": "Infertility and trouble conceiving" },
        { "desc": "Delayed growth" },
        { "desc": "Throat and chest infections" },
        { "desc": "Poor wound healing" },
        { "desc": "Acne and breakouts" },
      ],
      references: [
        { "url": "https://ods.od.nih.gov/factsheets/VitaminA-Consumer/" },
        { "url": "https://ods.od.nih.gov/factsheets/VitaminA-HealthProfessional/" },
      ]
    }],

    ["401", {
      value: 60, symbol: 'C', longName: 'Vitamin C',
      type: NutrientType.Vitamin,
      fatSoluble: false,
      essential: true,
      recommended: { men: "90mg", women: "75mg", children: "45-65mg" },
      blurb: "Also known as L-ascorbic acid, is a water-soluble vitamin that is naturally present in some foods, added to others, and available as a dietary supplement. Humans, unlike most animals, are unable to synthesize vitamin C endogenously, so it is an essential dietary component.",
      highDoses: "Taking more than 2,000 mg per day may cause abdominal cramps, diarrhea and nausea, as well as increase the risk of developing oxalate kidney stones in men",
      benefits: [
        { "desc": "Strong antioxidant that may reduce the risk of chronic diseases" },
        { "desc": "May help battle high blood pressure" },
        { "desc": "Fights heart disease risk factors, potentially lowering heart disease risk" },
        { "desc": "May lower bad LDL cholesterol and blood triglycerides" },
        { "desc": "Could reduce blood uric acid levels and help prevent gout attacks" },
        { "desc": "Helps prevent iron deficiencies by improving iron absorption" },
        { "desc": "Boosts immunity by helping white blood cells function better" },
        { "desc": "Protects your memory and thinking as you age" },
      ],
      deficiencies: [
        { "desc": "Rough bumpy skin" },
        { "desc": "Corkscrew shaped body hair" },
        { "desc": "Bright red hair follicles" },
        { "desc": "Spoon-Shaped fingernails with red spots or lines" },
        { "desc": "Dry, damaged skin" },
        { "desc": "Slowly healing wounds" },
        { "desc": "Painful, swollen joints" },
        { "desc": "Weak bones" },
        { "desc": "Bleeding gums and tooth loss" },
        { "desc": "Poor immunity" },
        { "desc": "Persistent iron deficiency anemia" },
        { "desc": "Fatigue and poor mood" },
        { "desc": "Unexplained weight gain" },
        { "desc": "Chronic inflammation and oxidative stress" },
      ],
      references: [
        { "url": "https://ods.od.nih.gov/factsheets/VitaminC-Consumer/" },
        { "url": "https://ods.od.nih.gov/factsheets/VitaminC-HealthProfessional/" },
      ]
    }],
    ["415", {
      value: 2, symbol: 'B6', longName: 'Vitamin B6',
      fatSoluble: false,
      type: NutrientType.Vitamin,
      essential: true,
      recommended: { men: "1.7mg", women: "1.5mg", children: ".5-1.0mg" },
      blurb: "Our bodies use B vitamins to convert the food we eat into the energy we need to function. Together the complex of eight B vitamins are important for metabolism, brain and liver function, growth, and building blood cells, as well as for maintaining healthy hair, skin, and vision.",
      highDoses: "Taking more than 1,000 mg of supplemental B6 a day may cause nerve damage and pain or numbness in the hands or feet. Some of these side effects have even been documented after just 100–300 mg of B6 per day.",
      benefits: [
        { "desc": "Proper brain development" },
        { "desc": "Regulates mood (helps body make serotonin)" },
        { "desc": "May help with depression" },
        { "desc": "May prevent and treat anemia by aiding hemoglobin production" },
        { "desc": "May be useful in treating symptoms of PMS" },
        { "desc": "May help treat nausea during pregnancy" },
        { "desc": "May help prevent cancer" },
        { "desc": "May promote eye health and prevent eye diseases" },
        { "desc": "May treat inflammation associated with rheumatoid arthritis" },
        { "desc": "Cope with stress (helps body make norepinephrine)" },
        { "desc": "Regulate sleep (helps body make melatonin)" },
        { "desc": "Along with B12 and Folate, may help slow cognitive decline and age-related memory loss (Alzheimer's)" },
      ],
      deficiencies: [
        { "desc": "Skin rashes" },
        { "desc": "Cracked and sore lips" },
        { "desc": "Sore glossy tongue" },
        { "desc": "Mood changes" },
        { "desc": "Weakened immune function" },
        { "desc": "Tired and low energy" },
        { "desc": "Tingling and Pain in Hands and Feet" },
        { "desc": "Seizures" },
        { "desc": "High Homocysteine (linked with heart disease and Alzheimer's)" },
      ],
      references: [
        { "url": "https://ods.od.nih.gov/factsheets/VitaminB6-HealthProfessional/" },
        { "url": "https://www.ncbi.nlm.nih.gov/pubmed/16320662" }
      ]
    }],
    ["323", {
      value: 20, symbol: 'E', longName: 'Vitamin E',
      type: NutrientType.Vitamin,
      fatSoluble: true,
      essential: true,
      recommended: { men: "15mg", women: "15mg", children: "4-15mg" },
      blurb: "A fat-soluble nutrient found in many foods. In the body, it acts as an antioxidant, helping to protect cells from the damage caused by free radicals. It is fairly rare to have a deficiency in this vitamin.",
      highDoses: "Increased risk of hemorrhagic stroke. It is not risky or harmful to eat this vitamin from food sources.",
      benefits: [
        { "desc": "Boost immune system" },
        { "desc": "Protect cells from damage (from smoking, air pollution and UV rays from sunlight)" },
        { "desc": "Longer cell life (helps reduce free radical damange)" },
        { "desc": "Widen blood vessels (help prevent blood clots)" },
        { "desc": "May help reduce hardening of the arteries (more research needed)" },
        { "desc": "May help reduce high blood pressure (more research needed)" },
        { "desc": "May help reduce heart disease (more research needed)" },
        { "desc": "May help reduce chances of certain cancers (more research needed)" },
        { "desc": "May help repair damaged cells (more research needed)" },
        { "desc": "May help prevent dementia (more research needed)" },
        { "desc": "May help prevent liver disease (more research needed)" },
      ],
      deficiencies: [
        { "desc": "Chron's disease" },
        { "desc": "Cystic fibrosis" },
        { "desc": "Loss of feeling in arms and legs" },
        { "desc": "Loss of body movement" },
        { "desc": "Vision problems" },
        { "desc": "Weakened immune system" },
      ],
      references: [
        { "url": "https://ods.od.nih.gov/factsheets/VitaminE-Consumer/" },
        { "url": "https://ods.od.nih.gov/factsheets/VitaminE-HealthProfessional/" },
        { "url": "https://medlineplus.gov/ency/article/002406.htm" }
      ]
    }],
    ["301", {
      value: 1000, symbol: 'Calcium (Ca)', longName: 'Calcium',
      type: NutrientType.Mineral,
      fatSoluble: true,
      essential: true,
      recommended: { men: "1000mg", women: "1000mg", children: "1000mg" },
      blurb: "Builds bones, enables our blood to clot and muscles to contract",
      highDoses: "Too much calcium may cause stomach upset.",
      benefits: [
        { "desc": "Supports bone health" },
        { "desc": "Helps balance blood pressure" },
        { "desc": "May help reduce colorectal cancer" },
        { "desc": "May help ease PMS" },
        { "desc": "Promotes dental health" },
        { "desc": "Combats kidney stones and kidney disease" },
        { "desc": "Promotes weight loss" }
      ],
      deficiencies: [
        { "desc": "Muscle aches and cramps" },
        { "desc": "Extreme fatigue" },
        { "desc": "Nail and skin problems" },
        { "desc": "Thin bones and diminished bone density (osteopenia and osteoporosis)" }
      ],
      references: [
        { "url": "https://www.nof.org/patients/treatment/calciumvitamin-d/" },
        { "url": "https://ods.od.nih.gov/factsheets/Calcium-HealthProfessional/" }
      ]
    }],
    ["421", {
      value: 0, symbol: 'Choline', longName: 'Choline',
      type: NutrientType.Vitamin,
      fatSoluble: false,
      essential: true,
      recommended: { men: "550mg", women: "425mg", children: "400mg" },
      blurb: "Impacts liver function, healthy brain development, muscle movement, your nervous system and metabolism",
      highDoses: "May cause headaches",
      benefits: [
        { "desc": "Helps with brain function" },
        { "desc": "Helps with liver function" },
        { "desc": "May lower inflammation" },
        { "desc": "Good for physical and mental performance" },
        { "desc": "Helps improve mood" },
        { "desc": "Helps improve symptoms of Alzheimer's and Parkinson's disease" },
        { "desc": "May help schizophrenia, bipolar and autism" },
      ],
      deficiencies: [
        { "desc": "Fatty deposits in the liver (non-alcoholic fatty liver disease)" },
        { "desc": "Low energy levels" },
        { "desc": "Memory loss" },
        { "desc": "Cognitive decline" },
        { "desc": "Learning disabilities" },
        { "desc": "Muscle aches" },
        { "desc": "Nerve damage" },
        { "desc": "Muscle aches" },
        { "desc": "Mood changes or disorders" },
      ],
      references: [
        { "url": "https://ods.od.nih.gov/factsheets/Choline-HealthProfessional/" },
        { "url": "https://www.ncbi.nlm.nih.gov/pmc/articles/PMC2518394/" },
      ]
    }],
    ["324", {
      value: 400, symbol: 'D', longName: 'Vitamin D',
      type: NutrientType.Vitamin,
      fatSoluble: true,
      essential: true,
      recommended: { men: "600IU", women: "600IU", children: "600IU" },
      blurb: "Essential for strong bones, because it helps the body use calcium from the diet.",
      highDoses: "Unlikely but can lead to hypercalcemia (high levels of calcium in the blood). This condition can cause serious problems, such as kidney failure, abnormal heart rhythms, and coma.",
      benefits: [
        { "desc": "Helps the body utilize calcium" },
        { "desc": "Helps with bone and muscle strength" },
        { "desc": "Helps control blood pressure and prevent artery damage" },
        { "desc": "May help reduce risk of certain cancers" },
        { "desc": "Helps with keeping a strong immune system" },
        { "desc": "May help reduce risk of developing diabetes" },
        { "desc": "May help fight the flu" },
      ],
      deficiencies: [
        { "desc": "Soft bones" },
        { "desc": "Bone pains and muscle weakness" },
        { "desc": "Increased risk of cardivascular disease" },
        { "desc": "Cognitive impairment in older adults" },
        { "desc": "Severe asthma in children" },
        { "desc": "Increased risk of cancer" },
      ],
      references: [
        { "url": "https://www.webmd.com/vitamins/ai/ingredientmono-929/vitamin-d" },
        { "url": "https://ods.od.nih.gov/factsheets/VitaminD-HealthProfessional/" },
      ]
    }],
    ["418", {
      value: 6, symbol: 'B-12', longName: 'Vitamin B-12',
      type: NutrientType.Vitamin,
      fatSoluble: false,
      essential: true,
      recommended: { men: "2.4mcg", women: "2.4mcg", children: "1mcg" },
      blurb: "Plays essential roles in red blood cell formation, cell metabolism, nerve function and the production of DNA",
      highDoses: "",
      benefits: [
        { "desc": "Helps with red blood cell formation and anemia prevention" },
        { "desc": "May prevent major birth defects" },
        { "desc": "May support bone health and prevent osteoporosis" },
        { "desc": "May reduce your risk of macular degeneration" },
        { "desc": "May improve mood and symptoms of depression" },
        { "desc": "May benefit your brain by preventing the loss of neurons" },
        { "desc": "May give you an energy boost" },
        { "desc": "May improve heart health by decreasing homocysteine" },
        { "desc": "Supports healthy hair, skin and nails" },
      ],
      deficiencies: [
        { "desc": "Pale or jaundiced skin" },
        { "desc": "Weakness and fatigue" },
        { "desc": "Sensations of pins and needles" },
        { "desc": "Changes to mobility" },
        { "desc": "Glossitis and mouth ulcers" },
        { "desc": "Breathlessness and Dizziness" },
        { "desc": "Disturbed or blurred vision " },
        { "desc": "Mood changes" },
        { "desc": "High temperature" },
      ],
      references: [
        { "url": "https://ods.od.nih.gov/factsheets/VitaminB12-HealthProfessional/" },
        { "url": "https://ods.od.nih.gov/factsheets/VitaminB12-Consumer/" },
      ]
    }],
    ["417", {
      value: 400, symbol: 'B9', longName: 'Vitamin B9 (Folate)',
      type: NutrientType.Vitamin,
      fatSoluble: false,
      essential: true,
      recommended: { men: "400mcg", women: "400mcg", children: "200mcg" },
      blurb: "Used for preventing and treating low blood levels of folate (folate deficiency), as well as its complications, including “tired blood” (anemia) and the inability of the bowel to absorb nutrients properly",
      highDoses: "Might cause abdominal cramps, diarrhea, rash, sleep disorders, irritability, confusion, nausea, stomach upset, behavior changes, skin reactions, seizures, gas, excitability, and other side effect",
      benefits: [
        { "desc": "Supports healthy fetal development and pregnancy" },
        { "desc": "May reduce the risk of cancer" },
        { "desc": "May prevent heart disease" },
        { "desc": "Acts as an antioxidant" },
        { "desc": "Enhances brain function" },
        { "desc": "Aids in red blood cell production" },
        { "desc": "Is a natural antidepressant" },
        { "desc": "May prevent hearing and vision loss" },
        { "desc": "is anti-aging" },
        { "desc": "Promotes fertility" },
        { "desc": "May ameliorate autism spectrum disorders" },
        { "desc": "Promotes a healthy immune system" },
        { "desc": "Attenuates liver damage" },
        { "desc": "Ameliorates kidney disease" },
        { "desc": "May promote bone strength" },
      ],
      deficiencies: [
        { "desc": "Rapidly dividing cells" },
        { "desc": "Symptoms of fatigue, weakness, and shortness of breath" },
        { "desc": "Gray hair" },
        { "desc": "Mouth sores" },
        { "desc": "Tongue swelling" },
        { "desc": "Growth problems" },
        { "desc": "Pale skin" },
        { "desc": "Irritability" },
        { "desc": "Weakness" },
        { "desc": "Lethargy" },
      ],
      references: [
        { "url": "https://ods.od.nih.gov/factsheets/Folate-HealthProfessional/" },
        { "url": "https://www.webmd.com/vitamins/ai/ingredientmono-1017/folic-acid" },
      ]
    }],
    ["304", {
      value: 400, symbol: 'Mg', longName: 'Magnesium (Mg)',
      type: NutrientType.Mineral,
      fatSoluble: true,
      essential: true,
      recommended: { men: "400mg", women: "320mg", children: "240mg" },
      blurb: "A crucial nutrient that’s responsible for over 300 biochemical reactions in your body",
      highDoses: "Can lead to gastrointestinal problems, such as diarrhea, nausea, and cramping",
      benefits: [
        { "desc": "Celluar reproduction" },
        { "desc": "Energy reproduction" },
        { "desc": "Supports bone health" },
        { "desc": "Helps with calcium absorption" },
        { "desc": "Helps minimize imact risk of diabetes" },
        { "desc": "Protects your heart" },
        { "desc": "May help minimize migraine headaches" },
        { "desc": "Help relieve symptoms of premenstrual syndrome" },
        { "desc": "Helps relieve anxiety" },
        { "desc": "Relieves insomnia" },
        { "desc": "Helps combat asthma" },
        { "desc": "Reduces high blood pressure" },
        { "desc": "Helps improve digestion and alleviate constipation symptoms" },
      ],
      deficiencies: [
        { "desc": "Loss of appetite" },
        { "desc": "Nausea and vomiting" },
        { "desc": "Fatigue and weakness" },
        { "desc": "Numbness and tingling" },
        { "desc": "Muscle cramps" },
        { "desc": "Seizures" },
        { "desc": "Personality changes" },
        { "desc": "Heart rhythm changes and spasms" },
      ],
      references: [
        { "url": "https://ods.od.nih.gov/factsheets/Magnesium-HealthProfessional/" },
        { "url": "https://ods.od.nih.gov/factsheets/Magnesium-Consumer/" },
        { "url": "https://www.algaecal.com/algaecal-ingredients/magnesium/" }
      ]
    }],
    ["315", {
      value: 2, symbol: 'Mn', longName: 'Manganese (Mn)',
      type: NutrientType.Mineral,
      fatSoluble: true,
      essential: true,
      recommended: { men: "2mg", women: "2mg", children: "1mg" },
      blurb: "Found in several foods including nuts, legumes, seeds, tea, whole grains, and leafy green vegetables and is considered an essential nutrient, because the body requires it to function properly",
      highDoses: "Excess  in the body can cause serious side effects, including symptoms resembling Parkinson's disease, such as shaking (tremors), consuming more than 11 mg per day could cause serious and harmful side effects",
      benefits: [
        { "desc": "May improve bone health in combination with other nutrients" },
        { "desc": "Strong antioxidant properties may reduce disease risk" },
        { "desc": "Helps reduce inflammation, particularly in combination with glucosamine and chondroitin" },
        { "desc": "Plays a role in blood sugar regulation" },
        { "desc": "Linked to lower incidences of epileptic seizures" },
        { "desc": "Plays a role in the metabolism of nutrients" },
        { "desc": "May reduce pms symptoms in combination with calcium" },
        { "desc": "May protect your brain against free radicals and improve brain function" },
        { "desc": "Contributes to good thyroid health" },
        { "desc": "May aid wound healing by playing a role in collagen production" },
      ],
      deficiencies: [
        { "desc": "Weak bones" },
        { "desc": "Stiff joints" },
        { "desc": "Anemia" },
        { "desc": "Weight loss" },
        { "desc": "Symptoms of PMS" },
      ],
      references: [
        { "url": "https://www.webmd.com/vitamins/ai/ingredientmono-182/manganese" },
        { "url": "https://www.healthline.com/nutrition/manganese-benefits" },
      ]
    }],
    ["406", {
      value: 20, symbol: 'B3', longName: 'Niacin (B3)',
      type: NutrientType.Vitamin,
      fatSoluble: true,
      essential: true,
      recommended: { men: "16mg", women: "14mg", children: "10mg" },
      blurb: "Acts as an antioxidant and plays a role in cell signaling and DNA repair",
      highDoses: "Various side effects, including nausea, vomiting, gout, blurred vision and liver toxicity",
      benefits: [
        { "desc": "Lowers LDL (bad) cholesterol by 5-20%" },
        { "desc": "Increases HDL (good) cholesterol by 15-35%" },
        { "desc": "Lowers triglycerides" },
        { "desc": "May help prevent heart disease" },
        { "desc": "May help treat type 1 diabetes" },
        { "desc": "Boosts brain function" },
        { "desc": "Improves skin function" },
        { "desc": "May reduce symptoms of arthritis" },
        { "desc": "Treats pellagra" },
      ],
      deficiencies: [
        { "desc": "Memory loss and mental confusion" },
        { "desc": "Fatigue" },
        { "desc": "Depression" },
        { "desc": "Headache" },
        { "desc": "Diarrhea" },
        { "desc": "Skin problems" },
      ],
      references: [
        { "url": "https://ods.od.nih.gov/factsheets/Niacin-HealthProfessional/" },
        { "url": "https://ods.od.nih.gov/factsheets/Niacin-Consumer/" },
        { "url": "http://jamanetwork.com/journals/jamainternalmedicine/fullarticle/216937" }
      ]
    }],
    ["410", {
      value: 10, symbol: 'B5', longName: 'Pantothenic acid (B5)',
      type: NutrientType.Vitamin,
      fatSoluble: false,
      essential: true,
      recommended: { men: "5mg", women: "5mg", children: "2mg" },
      blurb: "Essential for a healthy functioning nervous system throughout the whole body and the brain as well",
      highDoses: "Various side effects including diarrhea",
      benefits: [
        { "desc": "Skin reactions from radiation therapy" },
        { "desc": "Hormone stimulation" },
        { "desc": "Relieves stress" },
        { "desc": "Heart health" },
        { "desc": "Stamina" },
        { "desc": "Skin care and hair health" },
        { "desc": "Healthy immune system" },
        { "desc": "Cell turnover and cell regeneration" },
        { "desc": "Increased metabolic rate" },
        { "desc": "Helps lower the levels of bad cholesterol significantly" },
        { "desc": "May help reduce triglycerides in the blood also reduce fat retention" },
        { "desc": "May help speed up the wound healing properties" },
        { "desc": "Helps the body negate respiratory infections and fight off bacteria" },
        { "desc": "Rheumatoid arthritis revention" },
      ],
      deficiencies: [
        { "desc": "Headaches" },
        { "desc": "Fatigue" },
        { "desc": "Irritability" },
        { "desc": "Impaired muscle coordination" },
        { "desc": "Gastrointestinal problems" },
      ],
      references: [
        { "url": "https://ods.od.nih.gov/factsheets/PantothenicAcid-HealthProfessional/" },
        { "url": "https://ods.od.nih.gov/factsheets/PantothenicAcid-Consumer/" },
      ]
    }],
    ["305", {
      value: 1000, symbol: 'P', longName: 'Phosphorus (P)',
      type: NutrientType.Mineral,
      fatSoluble: false,
      essential: true,
      recommended: { men: "700mg", women: "700mg", children: "500mg" },
      blurb: "The second most plentiful mineral in your body, your body needs phosphorus for many functions, such as filtering waste and repairing tissue and cells",
      highDoses: "Can cause diarrhea, as well as a hardening of organs and soft tissue",
      benefits: [
        { "desc": "Keep your bones strong and healthy" },
        { "desc": "Help make energy" },
        { "desc": "Move your muscles" },
        { "desc": "Filter out waste in your kidneys" },
        { "desc": "Manage how your body stores and uses energy" },
        { "desc": "Grow, maintain, and repair tissue and cells" },
        { "desc": "Produce DNA and RNA — the body’s genetic building blocks" },
        { "desc": "Balance and use vitamins such as vitamins B and D, as well as other minerals like iodine, magnesium, and zinc" },
        { "desc": "Assist in muscle contraction" },
        { "desc": "Maintain a regular heartbeat" },
        { "desc": "Facilitate nerve conduction" },
        { "desc": "Reduce muscle pain after exercise" },
      ],
      deficiencies: [
        { "desc": "Joint or bone pain" },
        { "desc": "Loss of appetite" },
        { "desc": "Irritability or anxiety" },
        { "desc": "Fatigue" },
        { "desc": "Poor bone development in children" },
      ],
      references: [
        { "url": "https://medlineplus.gov/ency/article/002424.htm" },
        { "url": "https://www.healthline.com/health/phosphorus-in-diet" },
      ]
    }],
    ["405", {
      value: 1.7, symbol: 'B2', longName: 'Riboflavin (B2)',
      type: NutrientType.Vitamin,
      fatSoluble: false,
      essential: true,
      recommended: { men: "1.3mg", women: "1.1mg", children: "1mg" },
      blurb: "Riboflavin is a vitamin that is needed for growth and overall good health. It helps the body break down carbohydrates, proteins and fats to produce energy, and it allows oxygen to be used by the body.",
      highDoses: "Can cause the urine to turn a yellow-orange color and may also cause diarrhea",
      benefits: [
        { "desc": "Healthier skin" },
        { "desc": "Helps protect glutathione, which is an important antioxidant in the eye" },
        { "desc": "Promotes energy production" },
        { "desc": "Offers antioxidant protection" },
        { "desc": "Promotes iron metabolism" },
        { "desc": "May lower risk of developing cataracts" },
        { "desc": "May decrease homocysteine in the blood" },
        { "desc": "May help reduce the frequency of migraine headaches" },
      ],
      deficiencies: [
        { "desc": "Inflammation of the skin" },
        { "desc": "Swelling of soft tissue in the mouth" },
        { "desc": "Sore throat" },
        { "desc": "Cracks or sores on the outsides of the lips (cheilosis) and at the corners of the mouth" },
        { "desc": "Inflammation and redness of the tongue" },
        { "desc": "Scaly skin inflammation" },
        { "desc": "Formation of blood vessels in the clear covering of the eye" },
        { "desc": "Decreased red blood cell count" },
      ],
      references: [
        { "url": "https://ods.od.nih.gov/factsheets/Riboflavin-HealthProfessional/" },
        { "url": "https://ods.od.nih.gov/factsheets/Riboflavin-Consumer/" },
        { "url": "https://lpi.oregonstate.edu/mic/vitamins/riboflavin" }
      ]
    }], //Riboflavin
    // ["405", { value: 1.2, symbol: 'B2' }], //Riboflavin, in mg, 
    // ["405", { value: .0012, symbol: 'B2' }], //Riboflavin, in g, 
    ["317", {
      value: 70, symbol: 'Selenium (Se)', longName: 'Selenium (Se)',
      type: NutrientType.Mineral,
      fatSoluble: true,
      essential: true,
      recommended: { men: "55mcg", women: "55mcg", children: "40mcg" },
      blurb: "Plays a critical role in metabolism and thyroid function and also helps protect the body from damage caused by oxidative stress",
      highDoses: "Dizziness, nausea, vomiting, facial flushing and tremors",
      benefits: [
        { "desc": "Acts as a powerful antioxidant" },
        { "desc": "May reduce the risk of certain cancers" },
        { "desc": "May protect against heart disease" },
        { "desc": "Helps prevent mental decline" },
        { "desc": "Is important for thyroid health" },
        { "desc": "Boosts the immune system" },
        { "desc": "May help reduce asthma symptoms" },
        { "desc": "Increases longevity" },
        { "desc": "Can help boost fertility" },
      ],
      deficiencies: [
        { "desc": "Losing your hair" },
        { "desc": "Weight gain" },
        { "desc": "Higher risk for certain cancers" },
        { "desc": "Sick all the time" },
        { "desc": "Shortness of breath" },
      ],
      references: [
        { "url": "https://ods.od.nih.gov/factsheets/Selenium-HealthProfessional/" },
        { "url": "https://ods.od.nih.gov/factsheets/Selenium-Consumer/" },
      ]
    }],
    ["404", {
      value: 1.5, symbol: 'Thiamin (B1)', longName: 'Vitamin B1 (Thiamin)',
      type: NutrientType.Vitamin,
      fatSoluble: false,
      essential: true,
      recommended: { men: "1.2mg", women: "1.0mg", children: ".5mg" },
      blurb: "Plays a critical role in energy metabolism and, therefore, in the growth, development, and function of cells",
      highDoses: "Anaphylactic reactions have been observed with large intravenous doses",
      benefits: [
        { "desc": "Canker sores" },
        { "desc": "May help reduce mosquito's from biting" },
        { "desc": "Help prevent cataracts" },
        { "desc": "Helps prevent glaucoma and other vision problems" },
        { "desc": "Cerebellar syndrome, a type of brain damage" },
        { "desc": "Promotes energy production" },
        { "desc": "Protects nerves" },
        { "desc": "Prevents heart diseases" },
        { "desc": "Promotes digestion" },
        { "desc": "Prevents Alzheimer’s disease" },
        { "desc": "Improves memory" },
        { "desc": "Stimulates production of red blood cells" },
        { "desc": "Relieves effects of alcoholism" },
        { "desc": "Improves appetite" },
      ],
      deficiencies: [
        { "desc": "Loss of appetite" },
        { "desc": "Fatigue" },
        { "desc": "Irritability" },
        { "desc": "Reduced reflexes" },
        { "desc": "Tingling sensation in arms and legs" },
        { "desc": "Muscle weakness" },
        { "desc": "Blurry vision" },
        { "desc": "Nausea and vomiting" },
        { "desc": "Changes in heart rate" },
        { "desc": "Shortness of breath" },
        { "desc": "Delirium" },
      ],
      references: [
        { "url": "https://ods.od.nih.gov/factsheets/Thiamin-Consumer/" },
        { "url": "https://ods.od.nih.gov/factsheets/Thiamin-HealthProfessional/" },
        { "url": "https://www.healthline.com/nutrition/thiamine-deficiency-symptoms" }
      ]
    }],
    ["309", {
      value: 15, symbol: 'Zn', longName: 'Zinc',
      type: NutrientType.Mineral,
      fatSoluble: true,
      essential: true,
      recommended: { men: "11mg", women: "9mg", children: "4mg" },
      blurb: "Responsible for a number of functions in the human body, and it helps stimulate the activity of at least 100 different enzyme",
      highDoses: "Nausea, vomiting and diarrhea",
      benefits: [
        { "desc": "Helps regulate immune function" },
        { "desc": "Helps treat diarrhea" },
        { "desc": "Increases immunity and helps fight colds" },
        { "desc": "Acts as a powerful antioxidant that may help fight cancer" },
        { "desc": "Helps Balance Hormones" },
        { "desc": "Helps fight diabetes" },
        { "desc": "Maintains heart health by supporting blood vessels" },
        { "desc": "Prevents diarrhea" },
        { "desc": "Increases fertility" },
        { "desc": "Aids in nutrient absorption and digestion" },
        { "desc": "Supports liver health" },
        { "desc": "Helps with muscle growth and repair" },
      ],
      deficiencies: [
        { "desc": "Disease and illness" },
        { "desc": "Growth impediments and increased risk of infection" },
        { "desc": "Changes in appetite, including food cravings for salty or sweet foods" },
        { "desc": "Changes in ability to taste and smell" },
        { "desc": "Weight gain or loss" },
        { "desc": "Hair loss" },
        { "desc": "Digestive problems, including diarrhea" },
        { "desc": "Chronic fatigue syndrome" },
        { "desc": "Infertility" },
        { "desc": "Hormonal problems, including worsened PMS or menopause symptoms" },
        { "desc": "Low immunity" },
        { "desc": "Poor concentration and memory" },
        { "desc": "Slowed ability to heal wounds, skin infections or irritation" },
        { "desc": "Nerve dysfunction" },
      ],
      references: [
        { "url": "https://ods.od.nih.gov/factsheets/zinc-healthprofessional/" },
        { "url": "https://ods.od.nih.gov/factsheets/Zinc-Consumer/" },
        { "url": "https://www.medicalnewstoday.com/articles/263176.php" }
      ]
    }],
    ["312", {
      value: 2, symbol: 'Cu', longName: 'Copper',
      type: NutrientType.Mineral,
      fatSoluble: true,
      essential: true,
      recommended: { men: "900mcg", women: "900mcg", children: "900mcg" },
      blurb: "Helps maintain healthy bones, blood vessels, nerves, and immune function, and it contributes to iron absorption",
      highDoses: "Vomiting, bloody diarrhea, fever, stomach pain, low blood pressure, anemia, and heart problems",
      benefits: [
        { "desc": "Cardiovascular health" },
        { "desc": "Neuron signalling" },
        { "desc": "Immune function" },
        { "desc": "Osteoporosis" },
        { "desc": "Collagen production" },
        { "desc": "Arthritis" },
        { "desc": "Antioxidant action" },
        { "desc": "Protects cognitive function" },
        { "desc": "Promotes immune function" },
        { "desc": "Helps with bone density" },
        { "desc": "Helps with growth and development" },
        { "desc": "Helps with melanin production " },
        { "desc": "Faster skin healing" },
        { "desc": "Helps with iron absorption" },
        { "desc": "Aids in digestion" },
        { "desc": "Anti-Aging" },
        { "desc": "Energy booster" },
        { "desc": "Helps with thyroid function " },
        { "desc": "Helps protect against heart disease" },
        { "desc": "Cancer fighter" },
        { "desc": "Improves circulation" },
      ],
      deficiencies: [
        { "desc": "Anemia" },
        { "desc": "Low body temperature" },
        { "desc": "Bone fractures" },
        { "desc": "Osteoporosis" },
        { "desc": "Loss of skin pigmentation" },
        { "desc": "Thyroid problems" },
        { "desc": "Increased risk of infection" },
        { "desc": "Depigmentation of the hair and skin" },
      ],
      references: [
        { "url": "https://www.medicalnewstoday.com/articles/288165.php" },
        { "url": "https://lpi.oregonstate.edu/mic/minerals/copper" },
      ]
    }],
    ["306", {
      value: 3500, symbol: 'K', longName: 'Potassium',
      type: NutrientType.Mineral,
      fatSoluble: false,
      essential: true,
      recommended: { men: "4700mg", women: "4700mg", children: "4500mg" },
      blurb: "An electrolyte that counteracts the effects of sodium, helping to maintain consistent blood pressure",
      highDoses: "Stomach pain or nausea, shortness of breath, cold sweats, dizziness",
      benefits: [
        { "desc": "Helps regulate fluid balance" },
        { "desc": "Important for the nervous system" },
        { "desc": "Helps regulate muscle and heart contractions" },
        { "desc": "May help reduce blood pressure" },
        { "desc": "May help protect against strokes" },
        { "desc": "May help prevent osteoporosis" },
        { "desc": "May help prevent kidney stones" },
        { "desc": "May reduce water retention" },
        { "desc": "Stimulates neural activity" },
        { "desc": "Stabilizes blood sugar" },
        { "desc": "Boosts metabolism" },
        { "desc": "Reduces Anxiety and Stress" },
        { "desc": "Maintains water balance" },
      ],
      deficiencies: [
        { "desc": "Fatigue" },
        { "desc": "Muscle cramps" },
        { "desc": "Weakness" },
        { "desc": "Intestinal paralysis, which might lead to abdominal pain" },
        { "desc": "Bloating or abdominal cramping" },
        { "desc": "Constipation" },
        { "desc": "Nausea or vomiting" },
        { "desc": "Feeling thirsty much of the time" },
        { "desc": "Depression or confusion" },
        { "desc": "Abnormal heart rhythms" },
      ],
      references: [
        { "url": "https://ods.od.nih.gov/factsheets/Potassium-HealthProfessional/" },
        { "url": "https://ods.od.nih.gov/factsheets/Potassium-Consumer/" },
      ]
    }],
    ["307", {
      value: 2400, symbol: 'Na', longName: 'Sodium',
      type: NutrientType.Mineral,
      fatSoluble: false,
      essential: true,
      recommended: { men: "2400mg", women: "2400mg", children: "2300mg" },
      blurb: "Occurring in more than eighty different forms, it is needed for blood regulation and its absence can cause serious impairment of bodily functions",
      highDoses: "High blood pressure, swelling of the neural tissues and nerves, and cerebral edema",
      benefits: [
        { "desc": "Balances fluids in the body" },
        { "desc": "Helps send nerve impulses" },
        { "desc": "Needed for muscle contractions" },
        { "desc": "Influences blood pressure" },
        { "desc": "Prevents Sunstroke" },
        { "desc": "Improves brain function" },
        { "desc": "Relieves muscle cramps" },
        { "desc": "Helps with skin care" },
        { "desc": "Eliminates carbon dioxide in the body" },
        { "desc": "Helps control glucose absorption" },
        { "desc": "Helps maintains acid-base balance" },
      ],
      deficiencies: [
        { "desc": "Diarrhea" },
        { "desc": "Vomiting" },
        { "desc": "Weakness" },
        { "desc": "Low blood pressure" },
        { "desc": "Lethargy" },
        { "desc": "Weight loss" },
        { "desc": "Confusion" },
        { "desc": "Dizziness" },
        { "desc": "Muscular irritability" },
      ],
      references: [
        { "url": "https://www.heart.org/en/healthy-living/healthy-eating/eat-smart/sodium" },
        { "url": "https://www.cdc.gov/salt/index.htm" },
      ]
    }],
    ["204", {
      value: 65, symbol: 'Fat', longName: 'Fat',
      type: NutrientType.Macro,
      fatSoluble: true,
      essential: true,
      recommended: { men: "", women: "", children: "" },
      blurb: "",
      highDoses: "",
      benefits: [
        { "desc": "1" },
        { "desc": "2" },
      ],
      deficiencies: [
        { "desc": "1" },
        { "desc": "2" },
      ],
      references: [
        { "url": "" },
        { "url": "" },
        { "url": "" }
      ]
    }],
    ["428", {
      value: 80, symbol: 'K2', longName: 'Vitamin K2',
      type: NutrientType.Vitamin,
      fatSoluble: true,
      essential: false,
      recommended: { men: "120mcg", women: "90mcg", children: "60mcg" },
      blurb: "Otherwise known as menaquinone, it is found in animal foods and fermented foods, and not stored in the body fat as long as the other fat solubles. It may be found in animal food and fermented foods. Japanese Natto is extremely high in this nutrient.",
      highDoses: "May interfere with absorption of other nutrients",
      benefits: [
        { "desc": "Regulates calcium deposition" },
        { "desc": "Reduces blood vessel calcification" },
        { "desc": "Improves bone health" },
        { "desc": "Improves heart health" },
        { "desc": "Helps blood clot" },
        { "desc": "May help prevent heart disease" },
        { "desc": "May lower your risk of osteoporosis (porous bones)" },
        { "desc": "May improve dental health" },
        { "desc": "May help fight cancer" },
        { "desc": "Helps with menstrual pain and bleeding" },
        { "desc": "Improves brain function" },
        { "desc": "Improve your skin" },
      ],
      deficiencies: [
        { "desc": "Excessive bleeding" },
        { "desc": "Excessive bruising" },
        { "desc": "Oozing from gums or nose" },
        { "desc": "Heavy and painful menstrual periods" },
        { "desc": "Blood in the urine and/or stool" },
        { "desc": "Bone density loss" },
      ],
      references: [
        { "url": "https://ods.od.nih.gov/factsheets/VitaminK-HealthProfessional/" },
        { "url": "https://ods.od.nih.gov/factsheets/VitaminK-Consumer/" },
        { "url": "https://draxe.com/vitamin-k-deficiency/" },
        { "url": "https://www.healthline.com/nutrition/vitamin-k2" }
      ]
    }],
    ["430", {
      value: 80, symbol: 'K1', longName: 'Vitamin K1',
      type: NutrientType.Vitamin,
      fatSoluble: true,
      essential: true,
      recommended: { men: "120mcg", women: "90mcg", children: "60mcg" },
      blurb: "Also called phylloquinone, is mostly found in plant foods like leafy green vegetables (one study estimated that less than 10% of the K1 found in plants is actually absorbed)",
      highDoses: "May interfere with absorption of other nutrients",
      benefits: [
        { "desc": "Regulates calcium deposition" },
        { "desc": "Reduces blood vessel calcification" },
        { "desc": "Improves bone health" },
        { "desc": "Improves heart health" },
        { "desc": "Helps blood clot" },
        { "desc": "May help prevent heart disease" },
        { "desc": "May lower your risk of osteoporosis (porous bones)" },
        { "desc": "May improve dental health" },
        { "desc": "May help fight cancer" },
        { "desc": "Helps with menstrual pain and bleeding" },
        { "desc": "Improves brain function" },
        { "desc": "Improve your skin" },
      ],
      deficiencies: [
        { "desc": "Excessive bleeding" },
        { "desc": "Excessive bruising" },
        { "desc": "Oozing from gums or nose" },
        { "desc": "Heavy and painful menstrual periods" },
        { "desc": "Blood in the urine and/or stool" },
        { "desc": "Bone density loss" },
      ],
      references: [
        { "url": "https://ods.od.nih.gov/factsheets/VitaminK-HealthProfessional/" },
        { "url": "https://ods.od.nih.gov/factsheets/VitaminK-Consumer/" },
        { "url": "https://www.ncbi.nlm.nih.gov/pubmed/8813897" }
      ]
    }],
    /*["313", {
      value: 0, symbol: 'Flouride (Fl)', longName: 'Flouride (Fl)',
      type: NutrientType.Mineral,
      fatSoluble: true,
      essential: true,
      recommended: { men: "", women: "", children: "" },
      blurb: "",
      highDoses: "",
      benefits: [
        { "desc": "1" },
        { "desc": "2" },
      ],
      deficiencies: [
        { "desc": "1" },
        { "desc": "2" },
      ],
      references: [
        { "url": "" },
        { "url": "" },
        { "url": "" }
      ]
    }],*/
    ["203", {
      value: 0, symbol: 'Protein', longName: 'Protein',
      type: NutrientType.Macro,
      fatSoluble: false,
      essential: true,
      recommended: { men: ".4g per lb of bodyweight", women: ".4g per lb of bodyweight", children: ".4g per lb of bodyweight" },
      blurb: "An important component of every cell in the body - hair and nails are mostly made of protein. Your body uses it to build and repair tissues, make enzymes, hormones, and other body chemicals. It is an important building block of bones, muscles, cartilage, skin, and blood.",
      highDoses: "Increased bowel movements, nausea, thirst, bloating, cramps, reduced appetite, tiredness (fatigue) and headache",
      benefits: [
        { "desc": "Reduces appetite and hunger levels" },
        { "desc": "Increases muscle mass and strength" },
        { "desc": "Good for your bones" },
        { "desc": "Reduces cravings and desire for late-night snacking" },
        { "desc": "Boosts metabolism and increases fat burning" },
        { "desc": "Lowers your blood pressure" },
        { "desc": "Helps maintain weight loss" },
        { "desc": "Does not harm healthy kidneys" },
        { "desc": "Helps your body repair itself after injury" },
        { "desc": "Helps you stay fit as you age" },
      ],
      deficiencies: [
        { "desc": "Swollen and puffy skin (edema)" },
        { "desc": "Fat accumulation in liver cells (fatty liver)" },
        { "desc": "Skin, hair and nail problems" },
        { "desc": "Loss of muscle mass" },
        { "desc": "Greater risk of bone fractures" },
        { "desc": "Stunted growth in children" },
        { "desc": "Increased severity of infections" },
        { "desc": "Greater appetite and calorie intake" },
      ],
      references: [
        { "url": "https://www.ncbi.nlm.nih.gov/pubmed/15883438" },
        { "url": "https://www.ncbi.nlm.nih.gov/pubmed/15941879" },
        { "url": "https://www.ncbi.nlm.nih.gov/pubmed/18448177" },
        { "url": "https://www.ncbi.nlm.nih.gov/pubmed/16174292" },
        { "url": "https://www.ncbi.nlm.nih.gov/pubmed/15735253" },
      ]
    }],
    ["205", {
      value: 300, symbol: 'Carbs', longName: 'Carbohydrates',
      type: NutrientType.Macro,
      fatSoluble: true,
      essential: true,
      recommended: { men: "", women: "", children: "" },
      blurb: "",
      highDoses: "",
      benefits: [
        { "desc": "1" },
        { "desc": "2" },
      ],
      deficiencies: [
        { "desc": "1" },
        { "desc": "2" },
      ],
      references: [
        { "url": "" },
        { "url": "" },
        { "url": "" }
      ]
    }],
    ["269", {
      value: 300, symbol: 'Sugars', longName: 'Sugars',
      type: NutrientType.Macro,
      fatSoluble: true,
      essential: true,
      recommended: { men: "", women: "", children: "" },
      blurb: "",
      highDoses: "",
      benefits: [
        { "desc": "1" },
        { "desc": "2" },
      ],
      deficiencies: [
        { "desc": "1" },
        { "desc": "2" },
      ],
      references: [
        { "url": "" },
        { "url": "" },
        { "url": "" }
      ]
    }],
    ["291", {
      value: 300, symbol: 'Fiber', longName: 'Fiber',
      type: NutrientType.Macro,
      fatSoluble: true,
      essential: true,
      recommended: { men: "", women: "", children: "" },
      blurb: "",
      highDoses: "",
      benefits: [
        { "desc": "1" },
        { "desc": "2" },
      ],
      deficiencies: [
        { "desc": "1" },
        { "desc": "2" },
      ],
      references: [
        { "url": "" },
        { "url": "" },
        { "url": "" }
      ]
    }]

    // ["208", {value: 400, symbol: 'Energy'}]
  ]);


  public foodGroups = [
    {
      "num": "0100"
      , "desc": "Dairy and Egg Products"
    },
    {
      "num": "0200"
      , "desc": "Spices and Herbs"
    },
    {
      "num": "0300"
      , "desc": "Baby Foods"
    },
    {
      "num": "0400"
      , "desc": "Fats and Oils"
    },
    {
      "num": "0500"
      , "desc": "Poultry Products"
    },
    {
      "num": "0600"
      , "desc": "Soups Sauces and Gravies"
    },
    {
      "num": "0700"
      , "desc": "Sausages and Luncheon Meats"
    },
    {
      "num": "0800"
      , "desc": "Breakfast Cereals"
    },
    {
      "num": "0900"
      , "desc": "Fruits and Fruit Juices"
    },
    {
      "num": "1000"
      , "desc": "Pork Products"
    },
    {
      "num": "1100"
      , "desc": "Vegetables and Vegetable Products"
    },
    {
      "num": "1200"
      , "desc": "Nut and Seed Products"
    },
    {
      "num": "1300"
      , "desc": "Beef Products"
    },
    {
      "num": "1400"
      , "desc": "Beverages"
    },
    {
      "num": "1500"
      , "desc": "Finfish and Shellfish Products"
    },
    {
      "num": "1600"
      , "desc": "Legumes and Legume Products"
    },
    {
      "num": "1700"
      , "desc": "Lamb Veal and Game Products"
    },
    {
      "num": "1800"
      , "desc": "Baked Products"
    },
    {
      "num": "1900"
      , "desc": "Sweets"
    },
    {
      "num": "2000"
      , "desc": "Cereal Grains and Pasta"
    },
    {
      "num": "2100"
      , "desc": "Fast Foods"
    },
    {
      "num": "2200"
      , "desc": "Meals Entrees and Side Dishes"
    },
    {
      "num": "2500"
      , "desc": "Snacks"
    },
    {
      "num": "3500"
      , "desc": "American IndianAlaska Native Foods"
    },
    {
      "num": "3600"
      , "desc": "Restaurant Foods"
    }
  ];


  public foodGroupsByKey = new Map([
    ['Dairy and Egg Products', '0100'],
    ['Spices and Herbs', '0200'],
    ['Baby Foods', '0300'],
    ['Fats and Oils', '0400'],
    ['Poultry Products', '0500'],
    ['Soups, Sauces, and Gravies', '0600'],
    ['Sausages and Luncheon Meats', '0700'],
    ['Breakfast Cereals', '0800'],
    ['Fruits and Fruit Juices', '0900'],
    ['Pork Products', '1000'],
    ['Vegetables and Vegetable Products', '1100'],
    ['Nut and Seed Products', '1200'],
    ['Beef Products', '1300'],
    ['Beverages', '1400'],
    ['Finfish and Shellfish Products', '1500'],
    ['Legumes and Legume Products', '1600'],
    ['Lamb, Veal, and Game Products', '1700'],
    ['Baked Products', '1800'],
    ['Sweets', '1900'],
    ['Cereal Grains and Pasta', '2000'],
    ['Fast Foods', '2100'],
    ['Meals, Entrees, and Side Dishes', '2200'],
    ['Snacks', '2500'],
    ['American Indian/Alaska Native Foods', '3500'],
    ['Restaurant Foods', '3600'],
  ]);



}
